import { useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useChurch, useImportCalendar } from '../hooks/useChurches'
import { useAuthStore } from '../store/auth'
import { Wordmark } from '../components/ui/Wordmark'
import { ApiError } from '../lib/api'
import { MapPin, Globe, Phone, Check, ArrowRight, CalendarDays, Clock } from 'lucide-react'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'
import type { CalendarImportResult } from '../hooks/useChurches'

type Step = 1 | 2 | 3 | 4

function denominationLabel(key: string | null) {
  if (!key) return null
  return DENOMINATION_OPTIONS.find((d) => d.key === key)?.label ?? null
}

// ─── Step header ──────────────────────────────────────────────────────────────

function StepBar({ step }: { step: Step }) {
  return (
    <header className="flex items-center justify-between px-8 py-5 border-b border-line-soft">
      <Link to="/" className="no-underline">
        <Wordmark size={20} />
      </Link>
      <div className="flex items-center gap-2.5">
        <span className="text-[11px] font-semibold text-muted tracking-[0.05em] uppercase">
          Step {step} of 4
        </span>
        <div className="w-28 h-1 rounded-full bg-line overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-[width] duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>
      </div>
    </header>
  )
}

// ─── Step 1 — Confirm church info ─────────────────────────────────────────────

function StepConfirm({ church, slug, onNext }: { church: { name: string; city?: string | null; state?: string | null; denomination?: string | null; addressLine1?: string | null; phone?: string | null; websiteUrl?: string | null }; slug: string; onNext: () => void }) {
  const denomLabel = denominationLabel(church.denomination ?? null)

  return (
    <div>
      <h1 className="serif text-[38px] leading-[1.05] tracking-[-0.02em] text-ink m-0 mb-2">
        Does this look right?
      </h1>
      <p className="text-[14px] text-ink-2 leading-[1.6] m-0 mb-7">
        Review your church's information before we set things up. You can update any of it after.
      </p>

      <div className="rounded-2xl border border-line-soft bg-surface p-6 mb-5">
        <div className="serif text-[22px] font-semibold text-ink mb-4">{church.name}</div>
        <div className="flex flex-col gap-3">
          {(church.city || church.state) && (
            <div className="flex items-center gap-2.5 text-[13px] text-ink-2">
              <MapPin size={14} className="text-muted shrink-0" />
              {[church.addressLine1, church.city, church.state].filter(Boolean).join(', ')}
            </div>
          )}
          {denomLabel && (
            <div className="flex items-center gap-2.5 text-[13px] text-ink-2">
              <span className="text-muted shrink-0 text-[14px]">✦</span>
              {denomLabel}
            </div>
          )}
          {church.phone && (
            <div className="flex items-center gap-2.5 text-[13px] text-ink-2">
              <Phone size={14} className="text-muted shrink-0" />
              {church.phone}
            </div>
          )}
          {church.websiteUrl && (
            <div className="flex items-center gap-2.5 text-[13px] text-ink-2">
              <Globe size={14} className="text-muted shrink-0" />
              {church.websiteUrl}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onNext}
          className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-xl bg-primary text-on-primary border-0 text-[14px] font-semibold cursor-pointer font-sans"
        >
          Looks good, continue <ArrowRight size={14} />
        </button>
        <Link
          to={`/churches/${slug}/edit`}
          className="flex items-center justify-center h-11 px-4 rounded-xl border border-line text-ink text-[13px] font-medium no-underline"
        >
          Edit info
        </Link>
      </div>
    </div>
  )
}

// ─── Step 2 — Subscribe ───────────────────────────────────────────────────────

function StepSubscribe({ slug, claimed, onSkip }: { slug: string; claimed: boolean; onSkip: () => void }) {
  if (!claimed) {
    return (
      <div>
        <div className="w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center mb-5">
          <Clock size={18} className="text-primary" />
        </div>
        <h1 className="serif text-[38px] leading-[1.05] tracking-[-0.02em] text-ink m-0 mb-2">
          Verification pending
        </h1>
        <p className="text-[14px] text-ink-2 leading-[1.6] m-0 mb-7">
          Your church page is under review. Subscribing, RSVP management, dish coordination, and the rest of the toolkit unlock once our team approves your claim (usually within 1–2 business days). We'll email you when it's ready.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={onSkip}
            className="flex-1 h-11 rounded-xl bg-primary text-on-primary border-0 text-[14px] font-semibold cursor-pointer font-sans"
          >
            Continue for now
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="serif text-[38px] leading-[1.05] tracking-[-0.02em] text-ink m-0 mb-2">
        Unlock the full toolkit
      </h1>
      <p className="text-[14px] text-ink-2 leading-[1.6] m-0 mb-7">
        Your listing is live and public. A subscription unlocks RSVP management, dish coordination, day-of check-in, and the attendee broadcast email.
      </p>

      <div className="rounded-2xl bg-s2 border border-line-soft p-5 mb-5">
        {[
          'RSVP management with approval flow',
          'Potluck dish coordination and gap tracking',
          'Day-of check-in mode for hosts',
          'Broadcast emails to attendees',
          'Traffic dashboard — views, RSVPs, follower trends',
          'Import events from Google Calendar',
        ].map((item) => (
          <div key={item} className="flex items-center gap-2.5 mb-2.5 last:mb-0">
            <Check size={14} className="text-primary shrink-0" />
            <span className="text-[13px] text-ink-2">{item}</span>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-primary-soft border border-primary px-5 py-4 mb-5 text-center">
        <div className="text-[13px] font-semibold text-ink mb-0.5">14-day free trial</div>
        <div className="text-[12.5px] text-ink-2">No credit card required to start</div>
      </div>

      <div className="flex items-center gap-3">
        <Link
          to={`/churches/${slug}/billing`}
          className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-xl bg-primary text-on-primary text-[14px] font-semibold no-underline"
        >
          Start free trial <ArrowRight size={14} />
        </Link>
        <button
          onClick={onSkip}
          className="h-11 px-4 rounded-xl border border-line text-ink text-[13px] font-medium cursor-pointer bg-transparent font-sans"
        >
          Skip for now
        </button>
      </div>
      <p className="text-[12px] text-muted text-center mt-2.5">Cancel anytime. No commitment.</p>
    </div>
  )
}

// ─── Step 3 — Import events ───────────────────────────────────────────────────

function StepImport({ church, onSkip, onDone }: { church: { id: string }; onSkip: () => void; onDone: () => void }) {
  const [icalUrl, setIcalUrl] = useState('')
  const [result, setResult] = useState<CalendarImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const importCalendar = useImportCalendar(church.id)

  function handleImport(e: React.FormEvent) {
    e.preventDefault()
    setResult(null)
    setError(null)
    importCalendar.mutate(icalUrl.trim(), {
      onSuccess: (res) => { setResult(res.data) },
      onError: (err) => {
        if (err instanceof ApiError) setError(err.message)
        else setError(err instanceof Error ? err.message : 'Import failed')
      },
    })
  }

  return (
    <div>
      <div className="w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center mb-5">
        <CalendarDays size={18} className="text-primary" />
      </div>
      <h1 className="serif text-[38px] leading-[1.05] tracking-[-0.02em] text-ink m-0 mb-2">
        Already have events?
      </h1>
      <p className="text-[14px] text-ink-2 leading-[1.6] m-0 mb-7">
        If you post events to Google Calendar, paste your iCal URL below and we'll import them all at once. Takes about 30 seconds.
      </p>

      {/* Instructions */}
      <div className="rounded-xl bg-s2 border border-line-soft px-4 py-4 mb-5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted mb-3">
          How to find your iCal URL in Google Calendar
        </div>
        {[
          'Open Google Calendar in a desktop browser',
          'Click the ⚙ gear icon → Settings',
          'Click your church calendar name under "My calendars"',
          'Scroll to "Integrate calendar" → copy the "Secret address in iCal format" URL',
        ].map((s, i) => (
          <div key={i} className="flex gap-2.5 mb-2 last:mb-0 items-start">
            <span className="shrink-0 w-4 h-4 rounded-full bg-line text-[10px] font-semibold text-ink-2 flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <span className="text-[12.5px] text-ink-2 leading-[1.5]">{s}</span>
          </div>
        ))}
      </div>

      <form onSubmit={handleImport} className="flex flex-col gap-3 mb-4">
        <input
          type="url"
          value={icalUrl}
          onChange={(e) => { setIcalUrl(e.target.value); setResult(null); setError(null) }}
          placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
          className="w-full px-3 py-2.5 rounded-lg border border-line-soft bg-bg text-[13px] font-sans"
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={importCalendar.isPending || !icalUrl.trim()}
            className="flex-1 h-11 rounded-xl bg-primary text-on-primary border-0 text-[14px] font-semibold cursor-pointer disabled:opacity-50 font-sans"
          >
            {importCalendar.isPending ? 'Importing…' : 'Import events'}
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="h-11 px-4 rounded-xl border border-line text-ink text-[13px] font-medium cursor-pointer bg-transparent font-sans"
          >
            Skip
          </button>
        </div>
      </form>

      {error && (
        <div className="px-3 py-2.5 rounded-lg bg-danger-soft border border-danger-line text-danger-deep text-[13px] mb-3">
          {error}
        </div>
      )}

      {result && (
        <div className="px-4 py-3.5 rounded-xl bg-s2 border border-line-soft">
          <div className="text-[14px] font-semibold text-good-deep mb-1">
            ✓ {result.imported} event{result.imported !== 1 ? 's' : ''} imported
            {result.skipped > 0 && (
              <span className="font-normal text-muted text-[13px] ml-2">· {result.skipped} skipped (past or duplicate)</span>
            )}
          </div>
          {result.errors.length > 0 && (
            <ul className="m-0 pl-4 mt-1.5 text-danger-deep text-[12px] flex flex-col gap-0.5">
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          {result.imported > 0 && (
            <button
              onClick={onDone}
              className="mt-3 flex items-center gap-1.5 text-primary text-[13px] font-semibold cursor-pointer bg-transparent border-0 font-sans p-0"
            >
              Continue <ArrowRight size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Step 4 — Done ────────────────────────────────────────────────────────────

function StepDone({ church }: { church: { name: string; slug: string } }) {
  return (
    <div className="text-center">
      <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-6">
        <Check size={28} className="text-on-primary" strokeWidth={2.5} />
      </div>
      <h1 className="serif text-[38px] leading-[1.05] tracking-[-0.02em] text-ink m-0 mb-3">
        You're all set!
      </h1>
      <p className="text-[14px] text-ink-2 leading-[1.6] m-0 mb-8 max-w-[400px] mx-auto">
        {church.name} is live on Fellowship. Congregation members can now find your events and RSVP — all in one place.
      </p>
      <div className="flex flex-col items-center gap-3">
        <Link
          to={`/churches/${church.slug}/dashboard`}
          className="inline-flex items-center gap-1.5 h-12 px-7 rounded-xl bg-primary text-on-primary text-[15px] font-semibold no-underline"
        >
          Go to your dashboard <ArrowRight size={15} />
        </Link>
        <Link
          to={`/churches/${church.slug}`}
          className="text-[13px] text-primary no-underline font-medium"
        >
          View your public profile →
        </Link>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ChurchOnboardingPage() {
  const { slug } = useParams<{ slug: string }>()
  const { accessToken } = useAuthStore()
  const { data: churchData, isLoading } = useChurch(slug)
  const [step, setStep] = useState<Step>(1)

  if (!accessToken) return <Navigate to="/login" replace />

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <p className="text-[13px] text-muted">Loading…</p>
      </div>
    )
  }

  const church = churchData?.data

  if (!church) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center gap-3">
        <p className="text-[14px] text-ink-2">Church not found.</p>
        <Link to="/for-churches" className="text-primary text-[13px] no-underline font-medium">
          ← Back
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col font-sans">
      <StepBar step={step} />

      <div className="flex-1 flex items-start justify-center py-14 px-6">
        <div className="w-full max-w-[520px]">
          {step === 1 && (
            <StepConfirm church={church} slug={church.slug} onNext={() => setStep(2)} />
          )}
          {step === 2 && (
            <StepSubscribe slug={church.slug} claimed={church.claimed} onSkip={() => setStep(3)} />
          )}
          {step === 3 && (
            <StepImport church={church} onSkip={() => setStep(4)} onDone={() => setStep(4)} />
          )}
          {step === 4 && (
            <StepDone church={church} />
          )}
        </div>
      </div>
    </div>
  )
}
